import i18n, { t } from 'i18next'

import {
  isMultiValueOperator,
  OPERATOR_DEFINITIONS,
  OperatorType,
} from '../config'
import { FilterCondition, FilterStructure } from '../types'
import { JrModel } from '@/Atomic/Widgets/AiChatBot/core/engine/config'
import { SchemaAttribute, SchemaAttributes, SchemaRelation } from '@/Data/types'
import { askAi } from '@/utils/ai/aiBackendConnector'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { getLocalizedTitle } from '@/utils/schemaLocalization'

/**
 * A single condition as it reads in the UI (Entity / Attribute / Condition / Value).
 * Uses display labels rather than raw ids so it is meaningful to a human (and to an AI).
 */
export interface ReadableCondition {
  /** Display title of the entity the attribute lives on (e.g. "Person", "Account manager") */
  entity: string
  /** Display title of the attribute (e.g. "Contact since", "Status") */
  attribute: string
  /** Human label of the operator (e.g. "is one of", ">=", "=") */
  condition: string
  /** Human-readable value(s) as shown in the UI (e.g. "Active", "English", "True") */
  value?: string | string[]
  /**
   * True when the condition selects a specific related record by identity
   * (the "Record" pseudo-attribute). The value is the record's name, so this
   * reads as "<entity> is <value>" rather than filtering on a field.
   */
  isRecordReference?: boolean
}

export interface ReadableGroup {
  /** All conditions in a group are joined by AND */
  match: 'all'
  conditions: ReadableCondition[]
}

/**
 * Human-readable projection of a filter, mirroring exactly what the modal shows.
 * Groups are joined by OR ("match: any"); conditions within a group by AND.
 */
export interface ReadableFilter {
  /** Display title of the root entity being filtered */
  entity: string
  /** Groups are joined by OR */
  match: 'any'
  groups: ReadableGroup[]
}

const getEntityTitle = (entity: string | undefined): string => {
  if (!entity) return ''
  try {
    const { schemaGeneral } = getSchemaAttributes(getSchemaName(entity))
    const title = schemaGeneral?.title
    return typeof title === 'string' && title ? title : entity
  } catch {
    return entity
  }
}

/**
 * Resolve the label shown in the row's "Entity" dropdown for a condition.
 * For relation/reference conditions this is the relation's name (e.g. "Account
 * manager"), NOT the underlying entity type ("Contact"). Mirrors
 * buildAvailableEntities so the readable filter matches the UI.
 */
const getConditionEntityLabel = (
  condition: FilterCondition,
  entityName: string,
  baseSchemaAttributes?: SchemaAttributes,
  schemaRelations?: SchemaRelation[],
): string => {
  // Root entity (no relation): use its own display title.
  if (!condition.referenceField || condition.entity === entityName) {
    return getEntityTitle(condition.entity ?? entityName)
  }

  // m:1 reference field (e.g. "accountManager"): use the base attribute title.
  const refAttr = baseSchemaAttributes?.[condition.referenceField]
  if (refAttr?.title) return refAttr.title

  // Configured relation (m:n / 1:n): use the localized relation title.
  const relation = schemaRelations?.find(
    (rel) => rel.relationPath === condition.referenceField,
  )
  if (relation) {
    const fallback =
      relation.entityName.charAt(0).toUpperCase() + relation.entityName.slice(1)
    return getLocalizedTitle(relation, fallback)
  }

  // Fall back to the entity's display title.
  return getEntityTitle(condition.entity ?? entityName)
}

const getAttributeForCondition = (
  condition: FilterCondition,
  entityName: string,
  baseSchemaAttributes?: SchemaAttributes,
): SchemaAttribute | undefined => {
  // For the root entity we already have the attributes; for referenced
  // entities we resolve the (localized) schema of the condition's entity.
  const attributes =
    !condition.entity || condition.entity === entityName
      ? baseSchemaAttributes
      : (getSchemaAttributes(getSchemaName(condition.entity))
          .schemaAttributes as SchemaAttributes | undefined)

  return attributes?.[condition.field]
}

/**
 * Multi-value operators ("is one of" / "is none of") read more naturally as
 * their single-value equivalents ("=" / "!=") when only one value is selected.
 */
const collapseSingleValueOperator = (
  operator: string,
  value: string | string[] | undefined,
): string => {
  if (Array.isArray(value) && value.length === 1) {
    if (operator === 'in') return 'eq'
    if (operator === 'nin') return 'neq'
  }
  return operator
}

const enumTitleFor = (
  attribute: SchemaAttribute | undefined,
  value: string,
): string => {
  const match = attribute?.enumValues?.find(
    (e) => e.value === value || e.title === value,
  )
  return match?.title ?? value
}

const toReadableValue = (
  condition: FilterCondition,
  attribute: SchemaAttribute | undefined,
): string | string[] | undefined => {
  const operatorDef = OPERATOR_DEFINITIONS[condition.operator as OperatorType]
  if (operatorDef && !operatorDef.requiresValue) return undefined

  // Reference / record values: the resolved display label lives on the condition.
  if (condition.label) {
    return condition.label.includes(', ')
      ? condition.label.split(', ')
      : condition.label
  }

  const { value } = condition

  // Enum values -> map ids to their titles (dedupe titles exploded to many ids)
  if (attribute?.enumValues?.length) {
    const values = (Array.isArray(value) ? value : [value])
      .filter((v): v is string => typeof v === 'string')
      .map((v) => enumTitleFor(attribute, v))
    const unique = Array.from(new Set(values))
    if (isMultiValueOperator(condition.operator)) return unique
    return unique[0]
  }

  // Time-range values ({ amount, unit }) for inLast/inNext/notInLast/notInNext
  if (
    value &&
    typeof value === 'object' &&
    'amount' in value &&
    'unit' in value
  ) {
    const { amount, unit } = value as { amount: number; unit: string }
    return `${amount} ${unit}`
  }

  // "between": value is [from, to]. Join into an explicit range so the reader
  // (and the AI) can't mispair the bounds.
  if (condition.operator === 'between' && Array.isArray(value)) {
    const [from, to] = value
    return `${from ?? ''} – ${to ?? ''}`.trim()
  }

  if (Array.isArray(value)) {
    return value.map((v) => String(v))
  }

  if (value === null || value === undefined || value === '') return undefined

  return String(value)
}

/**
 * Build a human-readable, label-based projection of the (validated) internal
 * filter. Prefer this over the backend `toSavedFormat()` output when describing
 * a filter to a human or an AI: it uses display titles, keeps enum values as
 * their titles (not exploded ids), and resolves reference records to names.
 */
export const filterToReadable = (
  filter: FilterStructure,
  entityName: string,
  baseSchemaAttributes?: SchemaAttributes,
  schemaRelations?: SchemaRelation[],
): ReadableFilter => {
  return {
    entity: getEntityTitle(entityName),
    match: 'any',
    groups: filter.groups.map((group) => ({
      match: 'all',
      conditions: group.conditions
        .filter((condition) => condition.field)
        .map((condition) => {
          const attribute = getAttributeForCondition(
            condition,
            entityName,
            baseSchemaAttributes,
          )
          const value = toReadableValue(condition, attribute)

          // Collapse multi-value operators to their single-value equivalent
          // when only one value remains (e.g. "is one of [Active]" -> "= Active").
          const operator = collapseSingleValueOperator(
            condition.operator,
            value,
          )
          const operatorDef = OPERATOR_DEFINITIONS[operator as OperatorType]
          const isRecordReference = condition.field === '_id'

          return {
            entity: getConditionEntityLabel(
              condition,
              entityName,
              baseSchemaAttributes,
              schemaRelations,
            ),
            // "_id" is the record-selection pseudo-field shown as "Record".
            attribute: isRecordReference
              ? t('filter.record')
              : (attribute?.title ?? condition.field),
            condition: operatorDef ? t(operatorDef.labelKey) : operator,
            value:
              Array.isArray(value) && value.length === 1 ? value[0] : value,
            ...(isRecordReference && { isRecordReference: true }),
          }
        }),
    })),
  }
}

/**
 * The UI languages configured in i18n, discovered at runtime (see i18nInit).
 * Returns language codes, e.g. ["en", "de", "fr", "pl"].
 */
export const getUiLanguages = (): string[] => {
  const resources = i18n.options?.resources
  if (resources) return Object.keys(resources)
  // Fallbacks if resources aren't populated (e.g. async backend).
  return (
    i18n.options?.supportedLngs ||
    i18n.languages || [i18n.language || 'en']
  ).filter((lng) => lng !== 'cimode')
}

/** Map a language code to its English display name (e.g. "de" -> "German"). */
const languageDisplayName = (code: string): string => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code
  } catch {
    return code
  }
}

/** AI response shape: one description sentence per UI language code. */
export type FilterDescriptions = Record<string, string>

/** Builds a readable filter and asks the AI for a short description. */
export const getFilterDescription = (
  filter: FilterStructure,
  entityName: string,
  baseSchemaAttributes?: SchemaAttributes,
  schemaRelations?: SchemaRelation[],
): Promise<string> => {
  const readable = filterToReadable(
    filter,
    entityName,
    baseSchemaAttributes,
    schemaRelations,
  )
  const prompt = buildFilterDescriptionPrompt(readable, true)
  return askAi([{ role: 'user', content: prompt }], JrModel.Fast)
}

/**
 * Build the prompt sent to the AI to obtain a short, human-readable description
 * of a filter. Feed it the output of {@link filterToReadable}.
 *
 * By default generates descriptions for every UI language. Pass
 * `currentLanguageOnly: true` to generate only the active i18n language — the
 * AI will still reply with a JSON object, but with a single key.
 *
 * The AI must reply with a single JSON object keyed by language code
 * ({@link FilterDescriptions}) and nothing else.
 */
export const buildFilterDescriptionPrompt = (
  readable: ReadableFilter,
  currentLanguageOnly = false,
): string => {
  const languages = currentLanguageOnly
    ? [i18n.language || 'en']
    : getUiLanguages()
  const langLines = languages.map(
    (code) =>
      `  "${code}": "<the description in ${languageDisplayName(code)}>"`,
  )

  const dataLang = i18n.language || 'en'
  const dataLangNote =
    dataLang !== 'en'
      ? [
          'Note: the filter data (entity names, attribute names, values) is in',
          `${languageDisplayName(dataLang)}. Leave every data label exactly as`,
          'given — do NOT translate entity names, attribute names, enum labels, or',
          'record names, even in the other-language sentences. Translate only the',
          'surrounding prose.',
          '',
        ]
      : []

  return [
    'You are given a saved CRM filter as JSON. The entity type being filtered',
    'is given by the "entity" field in the JSON below.',
    '',
    'Structure:',
    '- The top-level "match" is "any": the record matches if ANY group matches (OR).',
    '- Each group\'s "match" is "all": every condition in the group must hold (AND).',
    '',
    'Each condition has:',
    '- "entity": what the condition is about — either the filtered entity itself',
    '  (matching the top-level "entity" field) or a related entity/relation',
    '  linked to it (e.g. an account manager, company, or address).',
    '- "attribute": the field being compared (e.g. "Status", "Contact since").',
    '- "condition": the operator (e.g. ">=", "is one of", "=", "is empty").',
    '- "value": the value(s) compared against, already shown as human-readable',
    '  labels (enum titles, record names, etc.) — not internal ids.',
    '- "isRecordReference": if present and true, the condition matches a SPECIFIC',
    '  related record by identity. Here "attribute" is just "Record" and "value"',
    '  is that record\'s name — read it as "<entity> is <value>" (e.g. "the',
    '  account manager is Anna Koch"), NOT as a field called "Record".',
    '',
    ...dataLangNote,
    'How to phrase it:',
    '- Write ONE concise, natural-language sentence PER language describing which',
    '  records this filter selects. Use the entity name from the JSON naturally.',
    '- Join conditions within a group with "and"; join groups with "or", and make',
    '  the boolean grouping unambiguous (e.g. "either … or …") when there are',
    '  multiple groups.',
    '- For a condition whose "entity" differs from the top-level entity, phrase it',
    '  as a property of that related entity (e.g. "whose address is in Germany"),',
    '  not of the main entity.',
    '- Normalize obvious value formatting, e.g. "1 years" → "1 year".',
    '- If a group has no conditions, ignore it. If an operator is unfamiliar,',
    '  paraphrase its intent.',
    '- Do not restate the JSON or list operators verbatim. Do not use the words',
    '  "group", "condition", "match", or "JSON" in the output. Phrase it the way a',
    '  person would describe the segment.',
    '',
    'Respond with ONLY a valid JSON object in exactly this shape, and nothing',
    'else — no markdown, no code fences, no commentary. The object must be',
    'parseable by a standard JSON parser; escape any quotes inside string values:',
    '{',
    langLines.join(',\n'),
    '}',
    '',
    'Filter:',
    JSON.stringify(readable, null, 2),
  ].join('\n')
}
